import routes from '@/store/router'

/**
 * Where should this user go after logging in?
 *
 * It used to be hard-coded to '/', which is MainDashboard — a permission-gated
 * route. A user whose preset does not grant it (ShopKeeper and FarmWorker do
 * not) logged in successfully, got a token, and was then bounced by the router
 * guard back to the page they came from. From the outside that looks exactly
 * like "login is broken": the form clears, nothing happens, no error.
 *
 * So resolve a route the user can actually reach instead of assuming one.
 *
 * @param {object} store  the Vuex store (needs getters.canRoute)
 * @returns {string} a path that will survive the router guard
 */
export function landingPath(store) {
  const canRoute = store?.getters?.canRoute
  if (typeof canRoute !== 'function') return '/'

  const reachable = (route) =>
    route.name &&
    !route.path.includes(':') &&        // no route that needs an id
    !route.meta?.guestOnly &&           // never land back on login
    (route.meta?.alwaysAllow || canRoute(route.name))

  // Preferred order: the most "home-like" screen for each kind of account
  // first, so a shop user lands on the shop and a farm user on the farm.
  const preferred = [
    'MainDashboard',
    'ShopDashboard',
    'FarmDashboard',
    'DeviceDashboard',
    'ShopProducts',
    'ShopPOS',
    'AnimalList',
    'DevicesList',
    'UserList',
  ]

  for (const name of preferred) {
    const route = routes.find(r => r.name === name)
    if (route && reachable(route)) return route.path
  }

  // Nothing preferred is granted — take the first route that is.
  const first = routes.find(reachable)
  if (first) return first.path

  // Nothing at all is granted. Fall back to a route that is never gated, so
  // the user lands somewhere real rather than on a blocked redirect.
  const open = routes.find(r => r.meta?.alwaysAllow && !r.path.includes(':'))
  return open ? open.path : '/'
}
